import { disablePushNotifications } from './push';
import { resumeSession, isLogoutPending, revokeBrowserSession } from '@/core';
import { z } from 'zod';
import { ApiError, createPkceHandshake, getSession, loadEnv, setSession } from '@/core';
import { CLINIC_PERSONA, type ClinicCompany, type ClinicUser } from '@/domain/session';
import { csrfToken, httpClient, toEpochMs } from './httpClient';
import {
  ChangeCurrentPasswordRequestSchema,
  UpdateCurrentUserRequestSchema,
  type ChangeCurrentPasswordInput,
  type UpdateCurrentUserInput,
} from './schemas/account';

const PKCE_VERIFIER_KEY = 'dt_pkce_verifier';
const PKCE_STATE_KEY = 'dt_pkce_state';

const TokenResponseSchema = z.object({
  accessToken: z.string().min(1),
  expiresAt: z.union([z.number(), z.string().datetime({ offset: true })]),
  // O BFF devolve o token CSRF no corpo: a SPA roda em outra origem (Pages) e não consegue ler
  // `document.cookie` do BFF. Campo garantido pelo contrato (BrowserSessionResponse.Csrf,
  // não-nulo em AuthEndpoints.cs) — obrigatório, como nos apps do Técnico e do Operacional.
  csrf: z.string().min(1),
});

const CsrfResponseSchema = z.object({ csrf: z.string().min(1) });

// Fronteira C4: forma real de `AuthMeResponse` da clin-api (Libraries/Application/Dtos/AuthDtos.cs).
// O backend serializa com WhenWritingNull, então campo ausente é `.optional()`, nunca `.nullable()`.
const CompanySchema = z.object({
  id: z.string().min(1),
  name: z.string().min(1),
  companyIdentificationCode: z.string().min(1),
  addressLine1: z.string().optional(),
  city: z.string().optional(),
  state: z.string().optional(),
  isOwner: z.boolean(),
});

const MeResponseSchema = z.object({
  id: z.string().min(1),
  nome: z.string().min(1),
  email: z.string().email(),
  ativo: z.boolean(),
  permissoes: z.array(z.object({ id: z.number(), nome: z.string(), descricao: z.string().optional() })).default([]),
  companyId: z.string().optional(),
  companies: z.array(CompanySchema).default([]),
});

function toCompany(raw: z.infer<typeof CompanySchema>): ClinicCompany {
  return {
    id: raw.id,
    name: raw.name,
    identificationCode: raw.companyIdentificationCode,
    city: raw.city,
    state: raw.state,
    isOwner: raw.isOwner,
  };
}

function toClinicUser(raw: z.infer<typeof MeResponseSchema>): ClinicUser {
  const companies = raw.companies.map(toCompany);
  return {
    id: raw.id,
    name: raw.nome,
    email: raw.email,
    active: raw.ativo,
    permissions: raw.permissoes.map((permission) => permission.nome),
    activeCompanyId: raw.companyId ?? companies[0]?.id,
    companies,
  };
}

export async function beginLogin(): Promise<void> {
  const env = loadEnv();
  if (isLogoutPending()) await logout();
  const handshake = await createPkceHandshake();
  // `sessionStorage` guarda o verifier do handshake em curso, nunca token de acesso ou refresh
  // (regra #24 / seguranca-frontend-web): o verifier é inútil sem o `code` e morre no callback.
  sessionStorage.setItem(PKCE_VERIFIER_KEY, handshake.codeVerifier);
  sessionStorage.setItem(PKCE_STATE_KEY, handshake.state);
  const params = new URLSearchParams({
    client_id: env.VITE_KEYCLOAK_CLIENT_ID,
    redirect_uri: `${window.location.origin}/auth/callback`,
    response_type: 'code',
    scope: 'openid profile email',
    state: handshake.state,
    code_challenge: handshake.codeChallenge,
    code_challenge_method: 'S256',
  });
  window.location.assign(`${env.VITE_KEYCLOAK_BASE_URL}/realms/${env.VITE_KEYCLOAK_REALM}/protocol/openid-connect/auth?${params.toString()}`);
}

export interface PendingHandshake {
  readonly code: string;
  readonly codeVerifier: string;
}

/** Consome (e apaga) o handshake PKCE, validando o `state` devolvido pelo Keycloak. */
export function consumeHandshake(code: string, state: string): PendingHandshake | null {
  const expectedState = sessionStorage.getItem(PKCE_STATE_KEY);
  const codeVerifier = sessionStorage.getItem(PKCE_VERIFIER_KEY);
  sessionStorage.removeItem(PKCE_STATE_KEY);
  sessionStorage.removeItem(PKCE_VERIFIER_KEY);
  if (!code || !codeVerifier || !expectedState || state !== expectedState) return null;
  return { code, codeVerifier };
}

export async function exchangeCode(handshake: PendingHandshake): Promise<void> {
  const env = loadEnv();
  const response = await fetch(`${env.VITE_BFF_BASE_URL}/auth/session`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      'X-CSRF-Token': csrfToken() ?? '',
    },
    body: JSON.stringify({ redirectUri: `${window.location.origin}/auth/callback`, persona: CLINIC_PERSONA, code: handshake.code, codeVerifier: handshake.codeVerifier }),
  });
  if (!response.ok) throw new Error('Não foi possível iniciar a sessão.');
  const parsed = TokenResponseSchema.safeParse(await response.json());
  if (!parsed.success) throw new Error('Resposta de autenticação inválida.');
  resumeSession();
  setSession({
    ...getSession(),
    accessToken: parsed.data.accessToken,
    expiresAt: toEpochMs(parsed.data.expiresAt),
    csrf: parsed.data.csrf,
  });
}

export async function loadCurrentUser(): Promise<ClinicUser> {
  const payload: unknown = await httpClient.get('/auth/me');
  const parsed = MeResponseSchema.safeParse(payload);
  if (!parsed.success) throw new Error('Resposta de usuário da clínica inválida.');
  return toClinicUser(parsed.data);
}

/** Atualiza somente o nome e o sobrenome da pessoa autenticada (`PUT /auth/me`). */
export async function updateCurrentUser(input: UpdateCurrentUserInput): Promise<ClinicUser> {
  const request = UpdateCurrentUserRequestSchema.parse(input);
  const payload: unknown = await httpClient.put('/auth/me', request);
  const parsed = MeResponseSchema.safeParse(payload);
  if (!parsed.success) throw new Error('Resposta de atualização do perfil em formato inesperado.');
  return toClinicUser(parsed.data);
}

/** Envia um código ao e-mail da sessão autenticada (`POST /auth/me/password/code`). */
export async function requestCurrentPasswordChangeCode(): Promise<void> {
  await httpClient.post<unknown>('/auth/me/password/code');
}

/** Confirma a troca autenticada de senha (`POST /auth/me/password`). */
export async function changeCurrentPassword(input: ChangeCurrentPasswordInput): Promise<void> {
  const request = ChangeCurrentPasswordRequestSchema.parse(input);
  await httpClient.post<unknown>('/auth/me/password', request);
}

export async function logout(): Promise<void> {
  try { await disablePushNotifications(); } finally { await revokeBrowserSession(loadEnv().VITE_BFF_BASE_URL); }
}

/**
 * Rehidratação do token CSRF da sessão de navegador (GET /auth/csrf).
 *
 * Num reload a memória da SPA é zerada, mas o cookie `__Host-dt_session` continua válido: o BFF
 * injeta o Authorization e o `GET /auth/me` responde 200, então a navegação segue — só que sem
 * `csrf` em memória toda mutação recebe 403 "Token CSRF ausente ou inválido". Este endpoint
 * devolve o token da sessão corrente (401 quando não há sessão). O valor continua APENAS em
 * memória (regra #24 / seguranca-frontend-web) e a resposta é validada por Zod na fronteira.
 *
 * Idempotente: não faz nada quando já há token disponível.
 */
export async function ensureCsrfToken(): Promise<void> {
  if (csrfToken() !== null) return;
  const env = loadEnv();
  let response: Response;
  try {
    response = await fetch(`${env.VITE_BFF_BASE_URL}/auth/csrf`, {
      method: 'GET',
      credentials: 'include',
      headers: { Accept: 'application/json' },
    });
  } catch {
    // Rede/BFF fora do ar não é sessão inválida: o chamador precisa distinguir para não deslogar
    // quem estava logado por causa de um blip.
    throw new ApiError({
      status: 0,
      correlationId: undefined,
      kind: 'network',
      message: 'Não foi possível conectar. Verifique sua rede ou tente novamente.',
    });
  }
  if (!response.ok) {
    // 401 = sessão de navegador realmente inválida; 5xx e demais falhas são transientes.
    throw new ApiError({
      status: response.status,
      correlationId: response.headers.get('X-Correlation-Id') ?? undefined,
      kind: 'http',
      message: response.status === 401
        ? 'Sua sessão expirou. Entre novamente.'
        : 'Não foi possível validar a proteção da sessão. Tente novamente em instantes.',
    });
  }
  const parsed = CsrfResponseSchema.safeParse(await response.json().catch(() => undefined));
  if (!parsed.success) throw new Error('Resposta de proteção CSRF inválida.');
  setSession({ ...getSession(), csrf: parsed.data.csrf });
}

// Fachada C4 para a apresentação, sem dependência direta de C2.
export { isLogoutPending };
